<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-info header-enhanced">
      <!-- Web Settings Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="System Status"
            type="gradient-info"
            sub-title="Online"
            icon="ni ni-check-bold"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">● All Systems</span>
              <span class="text-nowrap">Operational</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Maintenance Mode"
            type="gradient-warning"
            :sub-title="maintenance ? 'Active' : 'Inactive'"
            :icon="maintenance ? 'ni ni-settings' : 'ni ni-check-bold'"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span :class="maintenance ? 'text-warning' : 'text-success'" class="mr-2">
                {{ maintenance ? '●' : '●' }}
              </span>
              <span class="text-nowrap">{{ maintenance ? 'Under Maintenance' : 'Live' }}</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Last Updated"
            type="gradient-success"
            sub-title="2 min ago"
            icon="ni ni-watch-time"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">↻ Auto-sync</span>
              <span class="text-nowrap">Every 5 min</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="API Status"
            type="gradient-primary"
            sub-title="Healthy"
            icon="ni ni-world"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-primary mr-2">● Connected</span>
              <span class="text-nowrap">Response: 120ms</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-settings mr-2"></i>Web Settings & Configuration
          </h3>
          <p class="text-white-50 mb-0">Manage system settings and configurations</p>
        </b-col>
        <b-col class="text-right">
          <b-button
            variant="light"
            size="sm"
            class="shadow-sm"
            @click="refreshSettings">
            <i class="ni ni-refresh mr-1"></i>Refresh
          </b-button>
        </b-col>
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <!-- System Settings Cards -->
      <b-row>
        <b-col xl="8" class="mb-4">
          <b-card class="settings-card border-0 shadow">
            <template #header>
              <h5 class="mb-0 text-dark">
                <i class="ni ni-settings mr-2 text-primary"></i>
                System Configuration
              </h5>
            </template>

            <b-row>
              <b-col md="6" class="mb-4">
                <div class="setting-item">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 class="mb-1">
                        <i class="ni ni-settings-gear-65 mr-2 text-warning"></i>
                        Maintenance Mode
                      </h6>
                      <small class="text-muted">Enable/disable website maintenance</small>
                    </div>
                    <div class="setting-toggle">
                      <toggle-button
                        v-model="maintenance"
                        :sync="true"
                        @change="updateMaintenance($event)"
                        :width="80"
                        :height="35"
                        :labels="{
                          checked   : 'ON',
                          unchecked : 'OFF'
                        }"
                        :color="{
                          checked   : '#f5365c',
                          unchecked : '#2dce89',
                        }"
                      />
                    </div>
                  </div>
                  <div class="setting-status">
                    <b-badge :variant="maintenance ? 'danger' : 'success'">
                      {{ maintenance ? 'Maintenance Active' : 'Website Live' }}
                    </b-badge>
                  </div>
                </div>
              </b-col>

              <b-col md="6" class="mb-4">
                <div class="setting-item">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 class="mb-1">
                        <i class="ni ni-notification-70 mr-2 text-info"></i>
                        Email Notifications
                      </h6>
                      <small class="text-muted">System email alerts</small>
                    </div>
                    <div class="setting-toggle">
                      <toggle-button
                        v-model="emailNotifications"
                        :sync="true"
                        @change="updateEmailSettings($event)"
                        :width="80"
                        :height="35"
                        :labels="{
                          checked   : 'ON',
                          unchecked : 'OFF'
                        }"
                        :color="{
                          checked   : '#11cdef',
                          unchecked : '#8898aa',
                        }"
                      />
                    </div>
                  </div>
                  <div class="setting-status">
                    <b-badge :variant="emailNotifications ? 'info' : 'secondary'">
                      {{ emailNotifications ? 'Notifications Enabled' : 'Notifications Disabled' }}
                    </b-badge>
                  </div>
                </div>
              </b-col>

              <b-col md="6" class="mb-4">
                <div class="setting-item">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 class="mb-1">
                        <i class="ni ni-lock-circle-open mr-2 text-success"></i>
                        User Registration
                      </h6>
                      <small class="text-muted">Allow new user signups</small>
                    </div>
                    <div class="setting-toggle">
                      <toggle-button
                        v-model="userRegistration"
                        :sync="true"
                        @change="updateRegistrationSettings($event)"
                        :width="80"
                        :height="35"
                        :labels="{
                          checked   : 'ON',
                          unchecked : 'OFF'
                        }"
                        :color="{
                          checked   : '#2dce89',
                          unchecked : '#fb6340',
                        }"
                      />
                    </div>
                  </div>
                  <div class="setting-status">
                    <b-badge :variant="userRegistration ? 'success' : 'warning'">
                      {{ userRegistration ? 'Registration Open' : 'Registration Closed' }}
                    </b-badge>
                  </div>
                </div>
              </b-col>

              <b-col md="6" class="mb-4">
                <div class="setting-item">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 class="mb-1">
                        <i class="ni ni-chart-bar-32 mr-2 text-primary"></i>
                        Analytics Tracking
                      </h6>
                      <small class="text-muted">Google Analytics integration</small>
                    </div>
                    <div class="setting-toggle">
                      <toggle-button
                        v-model="analyticsEnabled"
                        :sync="true"
                        @change="updateAnalyticsSettings($event)"
                        :width="80"
                        :height="35"
                        :labels="{
                          checked   : 'ON',
                          unchecked : 'OFF'
                        }"
                        :color="{
                          checked   : '#5e72e4',
                          unchecked : '#8898aa',
                        }"
                      />
                    </div>
                  </div>
                  <div class="setting-status">
                    <b-badge :variant="analyticsEnabled ? 'primary' : 'secondary'">
                      {{ analyticsEnabled ? 'Analytics Active' : 'Analytics Disabled' }}
                    </b-badge>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <b-col xl="4" class="mb-4">
          <!-- System Information -->
          <b-card class="info-card border-0 shadow mb-4">
            <template #header>
              <h5 class="mb-0 text-dark">
                <i class="ni ni-chart-pie-35 mr-2 text-info"></i>
                System Information
              </h5>
            </template>

            <div class="system-info">
              <div class="info-item">
                <div class="d-flex justify-content-between">
                  <span class="info-label">Version</span>
                  <span class="info-value">v2.1.4</span>
                </div>
              </div>
              <div class="info-item">
                <div class="d-flex justify-content-between">
                  <span class="info-label">Environment</span>
                  <b-badge variant="success">Production</b-badge>
                </div>
              </div>
              <div class="info-item">
                <div class="d-flex justify-content-between">
                  <span class="info-label">Uptime</span>
                  <span class="info-value">7d 14h 32m</span>
                </div>
              </div>
              <div class="info-item">
                <div class="d-flex justify-content-between">
                  <span class="info-label">Database</span>
                  <b-badge variant="primary">Connected</b-badge>
                </div>
              </div>
              <div class="info-item">
                <div class="d-flex justify-content-between">
                  <span class="info-label">Cache</span>
                  <b-badge variant="success">Active</b-badge>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Quick Actions -->
          <b-card class="actions-card border-0 shadow">
            <template #header>
              <h5 class="mb-0 text-dark">
                <i class="ni ni-settings mr-2 text-warning"></i>
                Quick Actions
              </h5>
            </template>

            <div class="quick-actions">
              <b-button
                block
                variant="outline-primary"
                class="mb-2"
                @click="clearCache">
                <i class="ni ni-refresh mr-2"></i>Clear Cache
              </b-button>
              <b-button
                block
                variant="outline-info"
                class="mb-2"
                @click="backupDatabase">
                <i class="ni ni-cloud-upload-96 mr-2"></i>Backup Database
              </b-button>
              <b-button
                block
                variant="outline-success"
                class="mb-2"
                @click="exportLogs">
                <i class="ni ni-single-copy-04 mr-2"></i>Export Logs
              </b-button>
              <b-button
                block
                variant="outline-danger"
                @click="restartServices">
                <i class="ni ni-button-power mr-2"></i>Restart Services
              </b-button>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Dialog -->
      <v-dialog />

      <!-- Loading -->
      <AppLoaderAdmin :isLoading="loading" />
    </div>
  </div>
</template>

<script>
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';

  export default {
    data() {
      return {
        loading           : false,
        maintenance       : this.$store.getters['web/isMaintenance'] === 'true' ?
          true : false,
        emailNotifications: true,
        userRegistration  : true,
        analyticsEnabled  : true,
      }
    },
    components : {
      'AppLoaderAdmin' : () => import('@/components/layout/AppLoaderAdmin'),
      baseHeader,
      StatsCard,
    },
    methods : {

      /**
       * Update Maintenance
       */
      updateMaintenance(evt) {
        const action = evt.value === true ? 'ON' : 'OFF';
        const newVal = evt.value ? 'true' : 'false';
        this.$modal.show('dialog', {
          title : 'Website Settings',
          text  : 'Are you sure you want to switch ' + action +
            ' the maintenance mode?',
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.loading = true;
              this.$http.put('api/web/settings', {
                name  : 'maintenance',
                value : newVal,
              }).then(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'success',
                  title : 'Success',
                  text  : 'Updated website settings successfully',
                });
                this.$modal.hide('dialog');
              }).catch(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'error',
                  title : 'Failed',
                  text  : 'Oops! Something went wrong!',
                });
              }).finally(() => {
                this.loading = false;
              });
            },
          }, {
            title   : 'No',
            handler : () => {
              this.maintenance = !evt.value;
              this.$modal.hide('dialog');
            },
          } ],
        });
      },

      /**
       * Update Email Settings
       */
      updateEmailSettings(evt) {
        const action = evt.value === true ? 'enable' : 'disable';
        this.$notify({
          group : 'notif',
          type  : 'success',
          title : 'Success',
          text  : `Email notifications ${action}d successfully`,
        });
      },

      /**
       * Update Registration Settings
       */
      updateRegistrationSettings(evt) {
        const action = evt.value === true ? 'opened' : 'closed';
        this.$notify({
          group : 'notif',
          type  : 'success',
          title : 'Success',
          text  : `User registration ${action} successfully`,
        });
      },

      /**
       * Update Analytics Settings
       */
      updateAnalyticsSettings(evt) {
        const action = evt.value === true ? 'enabled' : 'disabled';
        this.$notify({
          group : 'notif',
          type  : 'success',
          title : 'Success',
          text  : `Analytics tracking ${action} successfully`,
        });
      },

      /**
       * Refresh Settings
       */
      refreshSettings() {
        this.loading = true;
        setTimeout(() => {
          this.loading = false;
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success',
            text  : 'Settings refreshed successfully',
          });
        }, 1000);
      },

      /**
       * Clear Cache
       */
      clearCache() {
        this.$modal.show('dialog', {
          title : 'Clear Cache',
          text  : 'Are you sure you want to clear the system cache?',
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.loading = true;
              setTimeout(() => {
                this.loading = false;
                this.$modal.hide('dialog');
                this.$notify({
                  group : 'notif',
                  type  : 'success',
                  title : 'Success',
                  text  : 'Cache cleared successfully',
                });
              }, 2000);
            },
          }, {
            title : 'No',
          } ],
        });
      },

      /**
       * Backup Database
       */
      backupDatabase() {
        this.$modal.show('dialog', {
          title : 'Database Backup',
          text  : 'Are you sure you want to create a database backup?',
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.loading = true;
              setTimeout(() => {
                this.loading = false;
                this.$modal.hide('dialog');
                this.$notify({
                  group : 'notif',
                  type  : 'success',
                  title : 'Success',
                  text  : 'Database backup completed successfully',
                });
              }, 3000);
            },
          }, {
            title : 'No',
          } ],
        });
      },

      /**
       * Export Logs
       */
      exportLogs() {
        this.$notify({
          group : 'notif',
          type  : 'success',
          title : 'Success',
          text  : 'System logs exported successfully',
        });
      },

      /**
       * Restart Services
       */
      restartServices() {
        this.$modal.show('dialog', {
          title : 'Restart Services',
          text  : 'Are you sure you want to restart all system services?',
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.loading = true;
              setTimeout(() => {
                this.loading = false;
                this.$modal.hide('dialog');
                this.$notify({
                  group : 'notif',
                  type  : 'warning',
                  title : 'Services Restarted',
                  text  : 'All system services have been restarted',
                });
              }, 5000);
            },
          }, {
            title : 'No',
          } ],
        });
      },
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/settings";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.9) 0%, rgba(0, 123, 255, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(23, 162, 184, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(0, 123, 255, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Settings Cards */
.settings-card {
  border-radius: 16px !important;
  animation: fadeInUp 0.8s ease-out;
}

.setting-item {
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  background: rgba(248, 249, 250, 0.5);
  transition: all 0.3s ease;
}

.setting-item:hover {
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.setting-toggle {
  margin-left: auto;
}

.setting-status {
  margin-top: 10px;
}

/* Info Card */
.info-card {
  border-radius: 12px !important;
  animation: fadeInUp 0.8s ease-out 0.3s both;
}

.system-info {
  padding: 0;
}

.info-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 500;
  color: #495057;
}

.info-value {
  font-weight: 600;
  color: #212529;
}

/* Actions Card */
.actions-card {
  border-radius: 12px !important;
  animation: fadeInUp 0.8s ease-out 0.6s both;
}

.quick-actions {
  padding: 10px 0;
}

.quick-actions .btn {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-bottom: 8px;
}

.quick-actions .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.quick-actions .btn-outline-primary:hover {
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.quick-actions .btn-outline-info:hover {
  box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
}

.quick-actions .btn-outline-success:hover {
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.quick-actions .btn-outline-danger:hover {
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .setting-item:hover {
    transform: none;
  }

  .quick-actions .btn:hover {
    transform: none;
  }

  .setting-item {
    padding: 15px;
  }

  .info-item {
    padding: 10px 0;
  }
}

/* Legacy styles */
.baseHeader, .header {
  margin-top: -80px;
}

.form-group {
  .large-font {
    margin-top: 0.25rem;
  }
}
</style>